// File: src/English/Grade3/Grade3EnglishLesson.js

import React, { useState, useEffect, useRef } from "react";
import { awardToken } from "../../awardtoken.js";
import { Link } from "react-router-dom";
import "./Grade3EnglishLesson.css";
import { asWord } from "../EnglishTool/quizHelpers.js";

// Optional user avatar, if you do user login
import defaultProfile from "../../UserProfile/EmptyProfile.jpg";

// 1) Pools and shuffle
import { getNounsPool } from "../EnglishTool/pool/nounsPool.js";
import { getVerbsPool } from "../EnglishTool/pool/verbsPool.js";
import { getAdjectivesPool } from "../EnglishTool/pool/adjectivesPool.js";
import { getAdverbsPool } from "../EnglishTool/pool/adverbsPool.js";
import { getReadingPool } from "../EnglishTool/pool/readingPool.js";
import { shuffleArray } from "../EnglishTool/shuffleArray.js";

import {
  buildNounTypeMC, buildNounClassifyMC, buildFillInBlankNoun, buildIdentifyNounsQ,
  buildVerbMC, buildFillInBlankVerb, buildIdentifyVerbsQ,
  buildAdjectiveMC, buildFillInBlankAdjective, buildIdentifyAdjectivesQ,
  buildAdverbMC, buildFillInBlankAdverb, buildIdentifyAdverbsQ,
  checkRequiredWord, checkSentenceStructure, makeSampleSentence,
} from "../EnglishTool/quizHelpers.js";
import { getSentencePool } from "../EnglishTool/pool/sentencePool.js";

function getTopicForChapter(ch) {
  switch (ch) {
    case 1: return "nouns";
    case 2: return "verbs";
    case 3: return "adjectives";
    case 4: return "adverbs";
    case 5: return "reading";
    default: return "nouns";
  }
}

/** 
 * 2) Chapter Pages for Grade3
 */
const chapterPages = {
  1: [
    // =============== CHAPTER 1: Nouns ===============
    {
      title: <h2>Nouns – What Are They?</h2>,
      body: (
        <>
          <p>
            A <strong>noun</strong> is a word that names a person, place,
            thing, or idea.
          </p>
          <p>
            Examples: <em>dog, John, school, happiness</em>
          </p>
          <p>
            Nouns often answer the questions <strong>“Who?”</strong> or{" "}
            <strong>“What?”</strong> in a sentence.
          </p>
        </>
      ),
    },
    {
      title: <h2>Nouns – Different Types</h2>,
      body: (
        <>
          <ul>
            <li>
              <strong>Common Nouns</strong> (boy, city, cat)
            </li>
            <li>
              <strong>Proper Nouns</strong> (Mike, Tokyo, Christmas) → Always
              capitalized.
            </li>
            <li>
              <strong>Concrete Nouns</strong> (apple, chair) → Things you can
              see or touch.
            </li>
            <li>
              <strong>Abstract Nouns</strong> (love, happiness) → Things you
              can’t see or touch (ideas, feelings).
            </li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Nouns – Practice</h2>,
      body: (
        <>
          <p>Try spotting nouns in a sentence:</p>
          <p>
            <em>
              "The children at the playground sang songs."
              <br />
              children, playground, songs = nouns
            </em>
          </p>
          <p>Identify if they’re common, proper, concrete, or abstract!</p>
        </>
      ),
    },
  ],

  2: [
    // =============== CHAPTER 2: Verbs ===============
    {
      title: <h2>Verbs – Action Words</h2>,
      body: (
        <>
          <p>
            A <strong>verb</strong> shows an action or a state of being
            (<em>run, jump, am, is</em>).
          </p>
          <p>Every sentence must have at least one verb!</p>
        </>
      ),
    },
    {
      title: <h2>Verbs – Tenses</h2>,
      body: (
        <>
          <p>
            Verbs can change form to show <strong>when</strong> something
            happens:
          </p>
          <ul>
            <li>
              <strong>Past:</strong> I <em>walked</em>.
            </li>
            <li>
              <strong>Present:</strong> I <em>walk</em>.
            </li>
            <li>
              <strong>Future:</strong> I <em>will walk</em>.
            </li>
          </ul>
          <p>Some verbs are irregular (e.g., <em>go → went</em>).</p>
        </>
      ),
    },
    {
      title: <h2>Verbs – Practice</h2>,
      body: (
        <>
          <p>Practice finding verbs and identifying their tense in a sentence:</p>
          <p>
            <em>
              "She quietly <strong>reads</strong> her book at the library."
              <br />
              reads = present tense verb
            </em>
          </p>
        </>
      ),
    },
  ],

  3: [
    // =============== CHAPTER 3: Adjectives ===============
    {
      title: <h2>Adjectives – Descriptive Words</h2>,
      body: (
        <>
          <p>
            An <strong>adjective</strong> describes a noun. It tells us what
            kind or how many.
          </p>
          <p>
            Examples: <em>big, happy, three</em>.
          </p>
        </>
      ),
    },
    {
      title: <h2>Adjectives – Comparison</h2>,
      body: (
        <>
          <p>Adjectives can change form for comparison:</p>
          <ul>
            <li>
              <strong>Positive:</strong> tall
            </li>
            <li>
              <strong>Comparative:</strong> taller
            </li>
            <li>
              <strong>Superlative:</strong> tallest
            </li>
          </ul>
          <p>
            Irregular examples: <em>good → better → best</em>.
          </p>
        </>
      ),
    },
    {
      title: <h2>Adjectives – Practice</h2>,
      body: (
        <>
          <p>Try describing an object with adjectives:</p>
          <p>
            <em>
              "The <strong>beautiful</strong>, <strong>old</strong> house on
              the hill."
            </em>
            <br />
            beautiful, old = adjectives
          </p>
        </>
      ),
    },
  ],

  4: [
    // =============== CHAPTER 4: Adverbs ===============
    {
      title: <h2>Adverbs – Overview</h2>,
      body: (
        <>
          <p>
            An <strong>adverb</strong> modifies a verb, adjective, or another
            adverb. It often tells <em>how, when, where</em>, or <em>how much</em>.
          </p>
          <p>
            Examples: <em>quickly, yesterday, outside, very</em>.
          </p>
        </>
      ),
    },
    {
      title: <h2>Adverbs – “How, When, Where”</h2>,
      body: (
        <>
          <ul>
            <li>
              <strong>How:</strong> slowly, happily
            </li>
            <li>
              <strong>When:</strong> today, later
            </li>
            <li>
              <strong>Where:</strong> here, everywhere
            </li>
          </ul>
          <p>Sometimes adverbs compare: <em>fast, faster, fastest</em>.</p>
        </>
      ),
    },
    {
      title: <h2>Adverbs – Practice</h2>,
      body: (
        <>
          <p>Identify adverbs in a sentence and what they modify:</p>
          <p>
            <em>
              "She <strong>quickly</strong> finished her work <strong>yesterday</strong>."
            </em>
            <br />
            quickly modifies “finished”; yesterday tells when it happened
          </p>
        </>
      ),
    },
  ],

  5: [
    // =============== CHAPTER 5: Reading ===============
    {
      title: <h2>Reading – Overview</h2>,
      body: (
        <>
          <p>
            <strong>Reading comprehension</strong> means understanding what
            you read.
          </p>
          <ul>
            <li>Find the main idea (what the passage is mostly about).</li>
            <li>Look for details that support or explain the main idea.</li>
            <li>Pay attention to new vocabulary and context clues.</li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Reading – Tips for Understanding</h2>,
      body: (
        <>
          <p>Ask yourself these questions when reading:</p>
          <ul>
            <li>Who or what is this about?</li>
            <li>What happened first, next, last?</li>
            <li>Why is this important?</li>
          </ul>
          <p>
            These questions help you figure out the text’s main idea and details.
          </p>
        </>
      ),
    },
    {
      title: <h2>Reading – Practice</h2>,
      body: (
        <>
          <p>
            "When the final bell rang, the students raced outside, excited to
            begin their summer break. Their teacher, smiling, wished them a
            happy vacation."
          </p>
          <p>
            <strong>Question:</strong> Why are the students running? What can
            you guess about their feelings?
          </p>
          <p>Think about the main idea and details!</p>
        </>
      ),
    },
  ],
};

export function generateChapterQuiz(topic, grade, n = 8) {
  // Sentence pool for IDENTIFY questions
  const sentencePool = getSentencePool(grade);

  // ============ Nouns ============
  if (topic === "nouns") {
    const nounPool = getNounsPool(grade);
    let questions = [
      buildNounTypeMC(nounPool, "common"),
      buildNounTypeMC(nounPool, "proper"),
      buildNounTypeMC(nounPool, "concrete"),
      buildNounTypeMC(nounPool, "abstract"),
      buildNounClassifyMC(nounPool),
      buildIdentifyNounsQ(sentencePool),
      buildFillInBlankNoun(shuffleArray(nounPool)[0]),
    ].filter(Boolean);
    return shuffleArray(questions).slice(0, n);
  }

  // ============ Verbs ============
  if (topic === "verbs") {
    const verbPool = getVerbsPool(grade);
    let questions = [
      buildVerbMC(verbPool, grade),                  // MC: Which is a verb?
      buildFillInBlankVerb(shuffleArray(verbPool)[0]), // FITB: Use ___ in a sentence
      buildIdentifyVerbsQ(sentencePool),              // IDENTIFY: Find verbs in a sentence
    ];
    // Optionally, add another MC or FITB if more variety is needed
    // (e.g., another buildVerbMC, or a random verb)
    if (verbPool.length > 1) questions.push(buildFillInBlankVerb(shuffleArray(verbPool)[1]));
    // Remove nulls, randomize, return n
    return shuffleArray(questions.filter(Boolean)).slice(0, n);
  }

  // ============ Adjectives ============
  if (topic === "adjectives") {
    const adjPool = getAdjectivesPool(grade);
    let questions = [
      buildAdjectiveMC(adjPool, grade),                      // MC: Which is an adjective?
      buildFillInBlankAdjective(shuffleArray(adjPool)[0]),   // FITB: Use ___ in a sentence
      buildIdentifyAdjectivesQ(sentencePool),                // IDENTIFY: Find adjectives
    ];
    if (adjPool.length > 1) questions.push(buildFillInBlankAdjective(shuffleArray(adjPool)[1]));
    return shuffleArray(questions.filter(Boolean)).slice(0, n);
  }

  // ============ Adverbs ============
  if (topic === "adverbs") {
    const advPool = getAdverbsPool(grade);
    let questions = [
      buildAdverbMC(advPool, grade),                         // MC: Which is an adverb?
      buildFillInBlankAdverb(shuffleArray(advPool)[0]),      // FITB: Use ___ in a sentence
      buildIdentifyAdverbsQ(sentencePool),                   // IDENTIFY: Find adverbs
    ];
    if (advPool.length > 1) questions.push(buildFillInBlankAdverb(shuffleArray(advPool)[1]));
    return shuffleArray(questions.filter(Boolean)).slice(0, n);
  }

  // ============ Reading ============
  if (topic === "reading") {
    const pool = getReadingPool(grade);
    const shuffled = shuffleArray(pool).slice(0, n);
    return shuffled.map((item) => {
      const qText = `What is the main idea of: "${item.statement}"?`;
      const correctAnswer = item.mainIdea;
      const distractors = [
        "No main idea given",
        "It’s about something else",
        "Nothing relevant",
        "Time travel",
        "Water shortage"
      ];
      const finalDist = shuffleArray(distractors).slice(0, 3);
      return {
        questionType: "MC",
        question: qText,
        correctAnswer,
        options: shuffleArray([correctAnswer, ...finalDist]),
      };
    });
  }

  // fallback (should not reach here)
  return [];
}

/** 4) Build Full Bank for Final Quiz */function buildFullQuestionBank(grade) {
  const bigSet = [];
  const sentencePool = getSentencePool(grade);

  // Nouns
  const nounPool = getNounsPool(grade);
  bigSet.push(
    buildNounTypeMC(nounPool, "common"),
    buildNounTypeMC(nounPool, "proper"),
    buildNounTypeMC(nounPool, "concrete"),
    buildNounTypeMC(nounPool, "abstract"),
    buildNounClassifyMC(nounPool),
    buildIdentifyNounsQ(sentencePool),
    buildFillInBlankNoun(shuffleArray(nounPool)[0])
  );

  // Verbs
  const verbPool = getVerbsPool(grade);
  bigSet.push(
    buildVerbMC(verbPool, grade),
    buildFillInBlankVerb(shuffleArray(verbPool)[0]),
    buildIdentifyVerbsQ(sentencePool)
  );

  // Adjectives
  const adjPool = getAdjectivesPool(grade);
  bigSet.push(
    buildAdjectiveMC(adjPool, grade),
    buildFillInBlankAdjective(shuffleArray(adjPool)[0]),
    buildIdentifyAdjectivesQ(sentencePool)
  );

  // Adverbs
  const advPool = getAdverbsPool(grade);
  bigSet.push(
    buildAdverbMC(advPool, grade),
    buildFillInBlankAdverb(shuffleArray(advPool)[0]),
    buildIdentifyAdverbsQ(sentencePool)
  );

  // Reading
  const readingPool = getReadingPool(grade);
  readingPool.forEach(item => {
    const qText = `What is the main idea of: "${item.statement}"?`;
    const correctAnswer = item.mainIdea;
    const distractors = [
      "No main idea given",
      "It’s about something else",
      "Nothing relevant",
      "Time travel",
      "Water shortage"
    ];
    const finalDist = shuffleArray(distractors).slice(0, 3);
    bigSet.push({
      questionType: "MC",
      question: qText,
      correctAnswer,
      options: shuffleArray([correctAnswer, ...finalDist])
    });
  });

  // Remove any nulls and shuffle
  return shuffleArray(bigSet.filter(Boolean));
}

function generateFinalQuiz(grade, count = 45) {
  const allQs = buildFullQuestionBank(grade);
  const n = Math.min(count, allQs.length);
  const shuffled = shuffleArray(allQs);
  return shuffled.slice(0, n); // never slice more than you have!
}

/** ================== STORY MODE COMPONENTS ================== **/
// For the Chapter Quiz
function StoryModeMultipleChoice({ cQ, onAnswerClick, currentScore, quizLength, user, lastFeedback, setLastFeedback }) {
  return (
    <div className="quick-quiz-story-mode">
      <div className="quiz-top-panel">
        <div className="teacher-image-container">
          <img src="/images/Teacher.png" alt="Teacher" className="quiz-character-img" />
        </div>
        <div className="quiz-question-box">
          <p className="quiz-question-text">{cQ.question}</p>
        </div>
      </div>
      <div className="quiz-bottom-panel">
        <div className="quiz-answer-box-left">
          {cQ.options.map((opt, idx) => (
            <button key={idx} className="quiz-answer-btn" onClick={() => onAnswerClick(asWord(opt))} disabled={lastFeedback && !lastFeedback.isCorrect} >
            {asWord(opt)}
              </button>
            ))}
        </div>
        <div className="student-image-container">
          <img src={user?.avatar ?? defaultProfile} alt="Student" className="quiz-character-img-student" />
        </div>
      </div>
      {lastFeedback && (
        <div style={{
          marginTop: 8, padding: 8, borderRadius: 8,
          background: lastFeedback.isCorrect ? "#e0ffe0" : "#fff0f0"
        }}>
          {lastFeedback.isCorrect
            ? <b style={{ color: "#228833" }}>Correct!</b>
            : <>
                <b style={{ color: "#c33" }}>Not quite.</b>
                <div>
                  The correct answer was: <b>{lastFeedback.correctAnswer}</b>
                </div>
                <button className="button1" style={{ marginTop: 8 }} onClick={() => setLastFeedback(null)}>Try Again</button>
              </>
          }
        </div>
      )}
      <p className="quiz-score-display">
        Score: {currentScore}/{quizLength}
      </p>
    </div>
  );
}

function StoryModeFillInBlank({ cQ, onSubmit, currentScore, quizLength, user, lastFeedback, setLastFeedback }) {
  const [userInput, setUserInput] = useState("");
  function handleSubmit() {
    onSubmit(userInput);
    // don't clear input until next question
  }

  return (
    <div className="quick-quiz-story-mode">
      <div className="quiz-top-panel">
        <div className="teacher-image-container">
          <img src="/images/Teacher.png" alt="Teacher" className="quiz-character-img" />
        </div>
        <div className="quiz-question-box">
          <p className="quiz-question-text">{cQ.question}</p>
        </div>
      </div>
      <div className="quiz-bottom-panel">
        <div className="quiz-answer-box-left">
          <input
            type="text"
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            placeholder="Type your answer"
            className="quiz-input"
            disabled={lastFeedback && !lastFeedback.isCorrect}
          />
          <button className="quiz-answer-btn" onClick={handleSubmit} disabled={lastFeedback && !lastFeedback.isCorrect}>
            Submit
          </button>
        </div>
        <div className="student-image-container">
          <img src={user?.avatar ?? defaultProfile} alt="Student" className="quiz-character-img-student" />
        </div>
      </div>
      {lastFeedback && (
        <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: lastFeedback.isCorrect ? "#e0ffe0" : "#fff0f0" }}>
          {lastFeedback.isCorrect ? (
            <b style={{ color: "#228833" }}>Correct!</b>
          ) : (
            <div>
              <b style={{ color: "#c33" }}>Not quite right:</b>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {lastFeedback.errors.map((err, idx) => <li key={idx}>{err}</li>)}
              </ul>
              <div style={{ marginTop: 5, fontStyle: "italic", color: "#444" }}>
                <b>Sample answer:</b> <span style={{ color: "#227" }}>{lastFeedback.sample}</span>
              </div>
              <button
                className="button1"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setUserInput("");
                  if (typeof window !== "undefined" && window.document) {
                    document.querySelector("input.quiz-input")?.focus();
                  }
                  // Clear feedback so they can retry
                  if (typeof setLastFeedback === "function") setLastFeedback(null);
                }}
              >Try Again</button>
            </div>
          )}
        </div>
      )}
      <p className="quiz-score-display">
        Score: {currentScore}/{quizLength}
      </p>
    </div>
  );
}

function StoryModeIdentify({ cQ, onSubmit, currentScore, quizLength, user, lastFeedback, setLastFeedback }) {
  const [userInput, setUserInput] = useState("");
  function handleSubmit() { onSubmit(userInput); }

  return (
    <div className="quick-quiz-story-mode">
      <div className="quiz-top-panel">
        <div className="teacher-image-container">
          <img src="/images/Teacher.png" alt="Teacher" className="quiz-character-img" />
        </div>
        <div className="quiz-question-box">
          <p className="quiz-question-text">{cQ.question}</p>
        </div>
      </div>
      <div className="quiz-bottom-panel">
        <div className="quiz-answer-box-left">
          <p>Type answers separated by commas</p>
          <input
            type="text"
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            placeholder="e.g. children, playground, songs"
            className="quiz-input"
            disabled={lastFeedback && !lastFeedback.isCorrect}
          />
          <button className="quiz-answer-btn" onClick={handleSubmit} disabled={lastFeedback && !lastFeedback.isCorrect}>
            Submit
          </button>
        </div>
        <div className="student-image-container">
          <img src={user?.avatar ?? defaultProfile} alt="Student" className="quiz-character-img-student" />
        </div>
      </div>
      {lastFeedback && (
        <div style={{
          marginTop: 8, padding: 8, borderRadius: 8,
          background: lastFeedback.isCorrect ? "#e0ffe0" : "#fff0f0"
        }}>
          {lastFeedback.isCorrect
            ? <b style={{ color: "#228833" }}>Correct!</b>
            : <>
                <b style={{ color: "#c33" }}>Not quite.</b>
                <div>{lastFeedback.message}</div>
                <div>
                  <b>Correct answer(s):</b> {cQ.correctAnswers.join(", ")}
                </div>
                <button className="button1" style={{ marginTop: 8 }} onClick={() => setLastFeedback(null)}>Try Again</button>
              </>
          }
        </div>
      )}
      <p className="quiz-score-display">
        Score: {currentScore}/{quizLength}
      </p>
    </div>
  );
}

function FinalQuiz({ cQ, fIdx, finalQs, handleFinalAnswer, fScore }) {
  const [input, setInput] = React.useState("");
  React.useEffect(() => setInput(""), [fIdx]);
  if (!cQ) return <div>Loading...</div>;
  return (
    <div>
      <p>Q {fIdx + 1}/{finalQs.length}: {cQ.question}</p>
      {cQ.questionType === "MC" && cQ.options && (
  cQ.options
    .filter(o => !!o && o.trim() !== "")
    .map((o, i) => (
      <button key={i} className="button1" onClick={() => handleFinalAnswer(o)}>
        {o}
      </button>
    ))
)}
      {(cQ.questionType === "FITB" || cQ.questionType === "IDENTIFY") && (
        <form onSubmit={e => { e.preventDefault(); handleFinalAnswer(input); }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={cQ.questionType === "IDENTIFY" ? "Type answers separated by commas" : "Type your answer"}
            style={{ minWidth: 240 }}
            autoFocus
          />
          <button className="button1" type="submit">Submit</button>
        </form>
      )}
      <p>Score: {fScore}</p>
      <br />
    </div>
  );
}

/** ================= MAIN COMPONENT ================= **/
export default function Grade3EnglishLesson({ onAwardTokens }) {
  const grade = 3;
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);
  const [chapDone, setChapDone] = useState(false);

  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);

  // Final quiz
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(360);
  const hasAwardedTokens = useRef(false);

  // Feeback State
  const [lastFeedback, setLastFeedback] = useState(null); // { result, errors:[], sample:string }

  // track if previously completed
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);
  // optional user for avatar
  const [user, setUser] = useState(null);

  // On mount
  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("loggedInUser"));
    if (savedUser) setUser(savedUser);

    const storedChap = localStorage.getItem("g3English_chapter");
    const storedPage = localStorage.getItem("g3English_pageIdx");
    const done = localStorage.getItem("grade3EnglishComplete");
    if (storedChap) setChapter(parseInt(storedChap, 10) || 1);
    if (storedPage) setPageIdx(parseInt(storedPage, 10) || 0);
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("g3English_chapter", String(chapter));
    localStorage.setItem("g3English_pageIdx", String(pageIdx));
  }, [chapter, pageIdx]);

  useEffect(() => {
    if (chapDone) {
      localStorage.setItem("grade3EnglishComplete", "true");
    }
  }, [chapDone]);

  // Final quiz timer
  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);
    }
  }, [showFinal, fFin, timeLeft]);

  // For progress bar
  function getTotalPages(obj) {
    return Object.values(obj).reduce((sum, arr) => sum + arr.length, 0);
  }
  function getAbsolutePage(obj, ch, pIdx) {
    let pagesBefore = 0;
    for (let c = 1; c < ch; c++) {
      pagesBefore += obj[c].length;
    }
    return pagesBefore + pIdx + 1;
  }
  const totalPages = getTotalPages(chapterPages);
  const absolutePage = getAbsolutePage(chapterPages, chapter, pageIdx);
  const progress = Math.round((absolutePage / totalPages) * 100);

  /** =========== CHAPTER QUIZ =========== */
  function startChapterQuiz() {
    const topic = getTopicForChapter(chapter);
    const generated = generateChapterQuiz(topic, grade, 8);
    setQuiz(generated);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
  }

  function handleAnswer(opt) {
    const cQ = quiz[qIdx];
  
    if (opt === cQ.correctAnswer) {
      setQScore(s => s + 1);
      setLastFeedback({ isCorrect: true, message: "Correct!", correctAnswer: cQ.correctAnswer });
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 600);
    } else {
      setLastFeedback({
        isCorrect: false,
        message: `Not quite. The correct answer was: "${cQ.correctAnswer}".`,
        correctAnswer: cQ.correctAnswer
      });
    }
  }
  

  function handleFillInBlank(userInput) {
    const { checkType, requiredWord, question } = quiz[qIdx];
    let errors = [];
    let isCorrect = false;
  
    // 1. Keyword check: allow basic forms for verbs
    const isVerbQ = /verb/i.test(question);
    if (!checkRequiredWord(userInput, requiredWord, isVerbQ)) {
      errors.push(`You must use the word "${requiredWord}"${isVerbQ ? " (any correct form is OK)" : ""} in your sentence.`);
    }
    // 2. Sentence structure (always check)
    errors = errors.concat(checkSentenceStructure(userInput));
    isCorrect = errors.length === 0;
  
    // Log for debugging
    console.log({ userInput, requiredWord, errors, isCorrect });
  
    setLastFeedback({
      userAnswer: userInput,
      isCorrect,
      errors,
      sample: makeSampleSentence(requiredWord, isVerbQ ? "verb" : checkType.includes("adj") ? "adjective" : checkType.includes("adv") ? "adverb" : "noun"),
    });
  
    if (isCorrect) {
      setQScore(s => s + 1);
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 800);
    }
  }
 
  function handleIdentifyWords(userInput) {
    const cQ = quiz[qIdx];
    const typed = userInput.split(",").map(s => s.trim().toLowerCase());
    const correctArr = (cQ.correctAnswers || []).map(w => w.toLowerCase());
  
    // What did the student get correct?
    const correctFound = typed.filter(w => correctArr.includes(w));
    const missed = correctArr.filter(w => !typed.includes(w));
    const extra = typed.filter(w => !correctArr.includes(w));
  
    const isCorrect = correctFound.length === correctArr.length && extra.length === 0;
  
    setLastFeedback({
      isCorrect,
      correctFound,
      missed,
      extra,
      correctAnswers: cQ.correctAnswers,
      message: isCorrect
        ? "Great job! You found all the correct words."
        : [
            missed.length > 0 ? `Missing: ${missed.join(", ")}` : null,
            extra.length > 0 ? `Extra (incorrect): ${extra.join(", ")}` : null,
          ].filter(Boolean).join(" | ")
    });
  
    if (isCorrect) {
      setQScore(s => s + 1);
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 900);
    }
  }
  
  function handleMCQuizFinish() {
    if (qScore >= quiz.length / 2) {
      // pass => next chapter or done
      if (chapter < 5) {
        setChapter(ch => ch + 1);
        setPageIdx(0);
      } else {
        setChapDone(true);
      }
    } else {
      alert("Below 50%. Please review and retake!");
    }
    // reset
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(false);
  }

  /** =========== FINAL QUIZ =========== */
  function startFinalQuiz() {
    const final = generateFinalQuiz(grade, 60);
    setFinalQs(final);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(360);
    setShowFinal(true);
  }

  function isIdentifyCorrect(userInput, correctAnswers) {
    if (!userInput || !correctAnswers) return false;
    // Accepts comma or space separated, trims, lowercases, filters out empties
    const typed = userInput.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
    const correctArr = correctAnswers.map(w => w.toLowerCase()).filter(Boolean);
    if (typed.length !== correctArr.length) return false;
    // Accept any order
    return typed.slice().sort().every((val, idx) => val === correctArr.slice().sort()[idx]);
  }

  
  function handleFinalAnswer(opt) {
    const cQ = finalQs[fIdx];
  
    // IDENTIFY-type question logic
    if (cQ.questionType === "IDENTIFY") {
      if (isIdentifyCorrect(opt, cQ.correctAnswers)) setFScore(s => s + 1);
      // always advance
    } else if (cQ.questionType === "FITB") {
      // Use the same helpers as chapter quiz
      const isVerbQ = /verb/i.test(cQ.question);
      const keywordOk = checkRequiredWord(opt, cQ.requiredWord, isVerbQ);
      const errors = checkSentenceStructure(opt);
      if (keywordOk && errors.length === 0) setFScore(s => s + 1);
      // always advance
    } else {
      if (opt === cQ.correctAnswer) setFScore(s => s + 1);
      // always advance
    }
  
    if (fIdx < finalQs.length - 1) setFIdx(i => i + 1);
    else setFFin(true);
  }
  
  
  

useEffect(() => {
  if (fFin && !hasAwardedTokens.current) {
    const percent = Math.round((fScore / finalQs.length) * 100);
    if (percent >= 80) {
      alert("You earned a token! (Pretend awarding code here)");
      awardToken();
    }

    // --- Save final quiz result for dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["english_g3"] = {
      ...(quizResults["english_g3"] || {}),
      finalQuizScore: fScore,
      finalQuizTotal: finalQs.length,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // -------------------------------------------

    hasAwardedTokens.current = true;
  }
}, [fFin, fScore, finalQs.length, onAwardTokens]);


  /** =========== RENDERs =========== */
  function ChapterSlide() {
    const currentPages = chapterPages[chapter];
    const page = currentPages[pageIdx];
    const isLastPage = pageIdx === currentPages.length - 1;
    return (
      <div>
        <h2>{page.title}</h2>
        <div>{page.body}</div>
        <div style={{ marginTop: 10 }}>
          {pageIdx > 0 && (
            <button className="button1" onClick={() => setPageIdx(p => p - 1)}>
              Back
            </button>
          )}
          {!isLastPage && (
            <button
              className="button1"
              onClick={() => setPageIdx(p => p + 1)}
              style={{ marginLeft: 10 }}
            >
              Next
            </button>
          )}
          {isLastPage && (
            <button
              className="button1"
              onClick={startChapterQuiz}
              style={{ marginLeft: 15 }}
            >
              Take Chapter {chapter} Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  function ChapterQuiz() {
    if (!quiz[qIdx]) return <p>Loading quiz...</p>;
    const cQ = quiz[qIdx];

    if (!quizFin) {
      switch (cQ.questionType) {
        case "MC":
          return (
            <StoryModeMultipleChoice
              cQ={cQ}
              onAnswerClick={handleAnswer}
              currentScore={qScore}
              quizLength={quiz.length}
              user={user}
              lastFeedback={lastFeedback}
              setLastFeedback={setLastFeedback}
            />
          );
        case "FITB":
          return (
            <StoryModeFillInBlank cQ={cQ} onSubmit={handleFillInBlank} currentScore={qScore}
            quizLength={quiz.length} user={user} lastFeedback={lastFeedback} setLastFeedback={setLastFeedback} />
          );
        case "IDENTIFY":
          return (
            <StoryModeIdentify
              cQ={cQ}
              onSubmit={handleIdentifyWords}
              currentScore={qScore}
              quizLength={quiz.length}
              user={user}
              lastFeedback={lastFeedback}
              setLastFeedback={setLastFeedback}
            />
          );
        default:
          return <p>Unknown question type</p>;
      }
    } else {
      // quiz finished
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={handleMCQuizFinish}>
            Continue
          </button>
        </div>
      );
    }
  }

  function ChapterUI() {
    if (!showQuiz) {
      return <ChapterSlide />;
    } else {
      return <ChapterQuiz />;
    }
  }

  return (
    <div className="grade3-english-page">

      {/* PROGRESS BAR */}
      <div style={{ margin: "10px 0" }}>
        <div
          style={{
            width: "80%",
            margin: "0 auto",
            background: "#ddd",
            borderRadius: 10,
            height: 20,
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              background: "#4caf50",
              height: 20,
              transition: "width 0.3s",
            }}
          />
        </div>
        <p>{progress}% complete</p>
      </div>

      {/* If previously done => short-circuit */}
      {hasCompletedBefore && !showQuiz && !showFinal && (
        <div style={{ margin: "15px 0", textAlign: "center" }}>
          <p>
            You previously completed all Grade 3 English lessons. You can jump
            to any chapter or retake the final quiz.
          </p>
          <div>
            {[1,2,3,4,5].map(ch => (
              <button
                key={ch}
                className="button1"
                onClick={() => {
                  setChapDone(false);
                  setChapter(ch);
                  setPageIdx(0);
                  setShowQuiz(false);
                  setShowFinal(false);
                }}
              >
                Go to Chapter {ch}
              </button>
            ))}
            <button className="button1" onClick={startFinalQuiz}>
              Go to Final Quiz
            </button>
          </div>
        </div>
      )}

      {/* Chapter + Quiz Flow */}
      {!chapDone && !showFinal && <ChapterUI />}

      {chapDone && !showFinal && (
        <div style={{ textAlign: "center" }}>
          <h3>All Chapters Completed!</h3>
          <button className="button1" onClick={startFinalQuiz}>
            Start Final Quiz
          </button>
        </div>
      )}

      {/* FINAL QUIZ */}
      {showFinal && (
  <FinalQuiz
    cQ={finalQs[fIdx]}
    fIdx={fIdx}
    finalQs={finalQs}
    handleFinalAnswer={handleFinalAnswer}
    fScore={fScore}
  />
)}

      <br />
      <Link to="/english" className="button1">← Back to English Page</Link>
    </div>
  );
}
